import { create } from 'zustand';
import type { RealTimeMessage } from '../api/types';
import mockSignalRServer from '../api/mockSignalR';
import { useDeviceStore } from './deviceStore';
import { useUIStore } from './uiStore';

interface RealTimeState {
  // Connection state
  isConnected: boolean;
  clientId: string | null;
  flatId: number | null;
  
  // Messages history
  messages: RealTimeMessage[];
  unreadCount: number;
  
  // Actions
  connect: (flatId?: number) => void;
  disconnect: () => void;
  sendMessage: (message: RealTimeMessage) => void;
  markAsRead: () => void;
  clearMessages: () => void;
}

export const useRealTimeStore = create<RealTimeState>((set, get) => ({
  // Initial state
  isConnected: false,
  clientId: null,
  flatId: null,
  messages: [],
  unreadCount: 0,

  // Connect to real-time service
  connect: (flatId?: number) => {
    const { isConnected } = get();
    const effectiveFlatId = flatId ?? 1;
    
    if (isConnected) {
      // Already connected, just update flatId
      set({ flatId: effectiveFlatId });
      return;
    }

    // Start SignalR server if not running
    mockSignalRServer.start();

    // Connect client
    const clientId = mockSignalRServer.connect(
      effectiveFlatId,
      // onMessage handler
      (message: RealTimeMessage) => {
        console.log('[RealTime] Received message:', message);
        
        // Add to messages
        set(state => ({
          messages: [message, ...state.messages].slice(0, 50), // Keep last 50 messages
          unreadCount: state.unreadCount + 1
        }));
        
        // Handle specific message types
        switch (message.type) {
          case 'deviceUpdate':
            handleDeviceUpdate(message);
            break;
          case 'safetyAlert':
            handleSafetyAlert(message);
            break;
          case 'scriptTriggered':
            handleScriptTriggered(message);
            break;
          case 'connectionStatus':
            set({ isConnected: message.isConnected ?? false });
            break;
        }
      },
      // onConnectionStateChanged handler
      (isConnected: boolean) => {
        console.log('[RealTime] Connection state:', isConnected);
        set({ isConnected });
        
        if (isConnected) {
          useUIStore.getState().addToast('Подключено к real-time сервису', 'success');
        } else {
          useUIStore.getState().addToast('Отключено от real-time сервиса', 'info');
        }
      }
    );

    set({ 
      clientId,
      flatId: effectiveFlatId,
      isConnected: true
    });

    console.log('[RealTime] Connected with clientId:', clientId);
  },

  // Disconnect from real-time service
  disconnect: () => {
    const { clientId } = get();
    
    if (clientId) {
      mockSignalRServer.disconnect(clientId);
    }
    
    set({ 
      isConnected: false,
      clientId: null,
      flatId: null
    });

    console.log('[RealTime] Disconnected');
  },

  // Send message (for testing)
  sendMessage: (message: RealTimeMessage) => {
    const { flatId } = get();
    if (flatId) {
      mockSignalRServer.broadcastToFlat(flatId, message);
    }
  },

  // Mark messages as read
  markAsRead: () => {
    set({ unreadCount: 0 });
  },

  // Clear message history
  clearMessages: () => {
    set({ messages: [], unreadCount: 0 });
  }
}));

// Handlers for different message types
function handleDeviceUpdate(message: RealTimeMessage) {
  const { deviceId, parameter, value } = message;
  
  if (!deviceId) return;
  
  // Update device in store
  const deviceStore = useDeviceStore.getState();
  const devices = deviceStore.devices;
  const device = devices.find(d => d.deviceId === deviceId);
  
  if (device) {
    // Update the specific parameter
    const updatedParams = device.parameters.map(p => {
      if (p.name === parameter) {
        return {
          ...p,
          boolValue: typeof value === 'boolean' ? value : p.boolValue,
          numericValue: typeof value === 'number' ? value : p.numericValue,
          stringValue: typeof value === 'string' ? value : p.stringValue,
          rusValueOnlyView: formatValue(value, p.type)
        };
      }
      return p;
    });
    
    const updatedDevice = { ...device, parameters: updatedParams };
    deviceStore.updateDeviceInList(updatedDevice);
    
    // Show toast notification
    if (message.message) {
      useUIStore.getState().addToast(message.message, 'info');
    }
  }
}

function handleSafetyAlert(message: RealTimeMessage) {
  if (message.message) {
    useUIStore.getState().addToast(
      `🚨 ${message.message}`,
      'error'
    );
  }
}

function handleScriptTriggered(message: RealTimeMessage) {
  if (message.message) {
    useUIStore.getState().addToast(message.message, 'success');
  }
}

function formatValue(value: any, type: string): string {
  switch (type) {
    case 'bool':
      return value ? 'Включено' : 'Выключено';
    case 'numeric':
      return `${value}`;
    default:
      return String(value);
  }
}
